import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, Lock, CheckCircle, Play, Eye, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Story } from '../types';
import realCasesService from '../services/realCasesService';
import './StoryBrowserPage.css';

const StoryBrowserPage: React.FC = () => {
  const navigate = useNavigate();
  const [stories, setStories] = useState<Story[]>([]);
  const [unlockedIds, setUnlockedIds] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>(['全部']);
  const [selectedCategory, setSelectedCategory] = useState<string>('全部');
  const [selectedDifficulty, setSelectedDifficulty] = useState<'全部' | 'easy' | 'medium' | 'hard'>('全部');
  const [showOnlyUnlocked, setShowOnlyUnlocked] = useState(false);

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      // 加载解锁状态
      const savedProgress = JSON.parse(localStorage.getItem('gameProgress') || '{}');
      const unlocked = savedProgress.unlockedStories || [];

      // 加载所有故事（全部分类）
      const casesData = await realCasesService.loadRealCases();
      const allStories: Story[] = [];
      for (const [groupCategory, storyArr] of Object.entries(casesData.turtleSoup)) {
        if (Array.isArray(storyArr)) {
          allStories.push(...storyArr.map(story => ({
            ...story,
            groupCategory,
            category: story.category || groupCategory
          })));
        }
      }
      setStories(allStories);
      setCategories(['全部', ...Object.keys(casesData.turtleSoup)]);

      // 全量开放普通题目（保留特殊剧情ID）
      const baseStoryIds = allStories.map(story => story.id).filter((id): id is string => Boolean(id));
      const mergedUnlockedIds = Array.from(new Set([...baseStoryIds, ...unlocked]));
      setUnlockedIds(mergedUnlockedIds);
      savedProgress.unlockedStories = mergedUnlockedIds;
      localStorage.setItem('gameProgress', JSON.stringify(savedProgress));
    } catch (error) {
      console.error('Error loading stories:', error);
    }
  };

  // 过滤故事（按主题组分类筛选）
  const filteredStories = stories.filter(story => {
    const categoryMatch = selectedCategory === '全部' || (story as any).groupCategory === selectedCategory;
    const difficultyMatch = selectedDifficulty === '全部' || story.difficulty === selectedDifficulty;
    const unlockedMatch = !showOnlyUnlocked || (story.id && unlockedIds.includes(story.id));
    return categoryMatch && difficultyMatch && unlockedMatch;
  });

  const getDifficultyLabel = (difficulty?: string) => {
    switch (difficulty) {
      case 'easy': return '简单';
      case 'medium': return '中等';
      case 'hard': return '困难';
      default: return '未知';
    }
  };

  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case 'easy': return 'from-green-100 to-green-200 text-green-700';
      case 'medium': return 'from-yellow-100 to-yellow-200 text-yellow-700';
      case 'hard': return 'from-red-100 to-red-200 text-red-700';
      default: return 'from-gray-100 to-gray-200 text-gray-700';
    }
  };

  const isUnlocked = (storyId: string) => unlockedIds.includes(storyId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-ocean-50 to-white">
      {/* 导航栏 */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="flex justify-between items-center max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回主页
          </button>
          <h1 className="text-xl font-bold flex items-center">
            <BookOpen className="w-6 h-6 mr-2 text-ocean-600" />
            题库总览
          </h1>
          <div className="text-sm text-gray-700">
            已开放 {unlockedIds.length} / {stories.length}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* 统计信息 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-ocean-600">{stories.length}</div>
            <div className="text-sm text-gray-600">总题数</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{unlockedIds.length}</div>
            <div className="text-sm text-gray-600">已开放</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">{categories.length - 1}</div>
            <div className="text-sm text-gray-600">主题分类</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{filteredStories.length}</div>
            <div className="text-sm text-gray-600">当前筛选</div>
          </div>
        </div>

        {/* 筛选器 */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center mb-4">
            <Filter className="w-5 h-5 mr-2 text-gray-600" />
            <h3 className="text-lg font-semibold">筛选条件</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {/* 分类筛选 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">题目分类</label>
              <div className="flex gap-2 flex-wrap">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      selectedCategory === category
                        ? 'bg-ocean-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* 难度筛选 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">难度等级</label>
              <div className="flex gap-2">
                {['全部', 'easy', 'medium', 'hard'].map(difficulty => (
                  <button
                    key={difficulty}
                    onClick={() => setSelectedDifficulty(difficulty as any)}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      selectedDifficulty === difficulty
                        ? 'bg-ocean-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {difficulty === '全部' ? '全部' : getDifficultyLabel(difficulty)}
                  </button>
                ))}
              </div>
            </div>

            {/* 解锁状态 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">显示状态</label>
              <button
                onClick={() => setShowOnlyUnlocked(!showOnlyUnlocked)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  showOnlyUnlocked
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {showOnlyUnlocked ? '仅已开放' : '全部题目'}
              </button>
            </div>
          </div>
        </div>

        {/* 故事列表 */}
        <div className="grid md:grid-cols-2 gap-6">
          {filteredStories.map((story, index) => {
            const unlocked = story.id ? isUnlocked(story.id) : false;
            return (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl shadow-lg overflow-hidden ${
                  !unlocked ? 'opacity-75' : ''
                }`}
              >
                {/* 状态条 */}
                <div className={`h-2 ${unlocked ? 'bg-green-500' : 'bg-gray-300'}`} />

                <div className="p-6">
                  {/* 头部 */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-gray-800">{story.title}</h3>
                        {unlocked && <CheckCircle className="w-5 h-5 text-green-500" />}
                        {!unlocked && <Lock className="w-5 h-5 text-gray-400" />}
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span className={`px-3 py-1 rounded-full font-medium bg-gradient-to-r ${getDifficultyColor(story.difficulty)}`}>
                          {getDifficultyLabel(story.difficulty)}
                        </span>
                        <span className="text-gray-500">{story.category}</span>
                      </div>
                    </div>
                  </div>

                  {/* 题目描述 */}
                  <p className="text-gray-600 mb-4">{story.surface}</p>

                  {/* 底部操作 */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="text-sm text-gray-500">
                      {unlocked ? '已开放 - 可以开始游戏' : '未开放'}
                    </div>
                    <div className="flex gap-2">
                      {unlocked ? (
                        <>
                          <button
                            onClick={() => navigate(`/game?story=${story.id}`)}
                            className="flex items-center gap-1 px-4 py-2 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition"
                          >
                            <Play className="w-4 h-4" />
                            开始
                          </button>
                          <button
                            onClick={() => navigate(`/game?story=${story.id}&preview=true`)}
                            className="flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                          >
                            <Eye className="w-4 h-4" />
                            预览
                          </button>
                        </>
                      ) : (
                        <button
                          disabled
                          className="px-4 py-2 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed"
                        >
                          <Lock className="w-4 h-4 inline mr-1" />
                          未开放
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* 空状态 */}
        {filteredStories.length === 0 && (
          <div className="text-center py-20">
            <BookOpen className="w-20 h-20 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">没有找到符合条件的题目</p>
            <button
              onClick={() => {
                setSelectedCategory('全部');
                setSelectedDifficulty('全部');
                setShowOnlyUnlocked(false);
              }}
              className="mt-4 px-6 py-2 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition"
            >
              清除筛选条件
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default StoryBrowserPage;
